import { useEffect, useState } from "react";
import { Play, X, ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { useReveal } from "@/hooks/useReveal";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const FILTERS = [
  { key: "all", label: "Tout" },
  { key: "battle", label: "Battles" },
  { key: "spectacle", label: "Spectacles" },
  { key: "stage", label: "Stages" },
];

export const mediaSrc = (item) => {
  if (item.type === "video") return item.thumb;
  return item.src?.startsWith("/api/") && BACKEND
    ? `${BACKEND}${item.src}`
    : item.src;
};

export function Gallery() {
  const head = useReveal();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [active, setActive] = useState(null);

  useEffect(() => {
    api
      .get("/gallery")
      .then(({ data }) => {
        if (Array.isArray(data)) {
          setItems(data);
        } else {
          console.error("Invalid /gallery response:", data);
          setItems([]);
        }
      })
      .catch((error) => {
        console.error("Unable to load gallery:", error);
        setItems([]);
      });
  }, []);

  const shown =
    filter === "all" ? items : items.filter((i) => i.category === filter);

  return (
    <section
      id="galerie"
      data-testid="gallery-section"
      className="relative py-20 md:py-28 bg-ink-2"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        <div ref={head.ref} className={head.className}>
          <p className="font-mono2 text-xs uppercase tracking-[0.3em] text-emerald-bright mb-3">
            En images
          </p>
          <h2 className="font-display font-bold text-3xl md:text-5xl tracking-tight max-w-3xl">
            Battles & <span className="text-mustard">spectacles</span>
          </h2>
          <p className="mt-5 max-w-2xl text-cream/70 leading-relaxed">
            Les moments forts de la compagnie : battles, scènes, stages. Clique
            pour voir en grand.
          </p>
        </div>

        <div
          className="flex flex-wrap gap-2 mt-8"
          data-testid="gallery-filters"
        >
          {FILTERS.map((f) => (
            <button
              key={f.key}
              data-testid={`gallery-filter-${f.key}`}
              onClick={() => setFilter(f.key)}
              className={`rounded-full px-4 py-1.5 text-xs font-mono2 uppercase tracking-wider border transition ${filter === f.key ? "bg-mustard text-ink border-mustard" : "border-white/10 text-cream/60 hover:border-mustard/50 hover:text-cream"}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div
          className="columns-2 md:columns-3 lg:columns-4 gap-4 mt-8 [column-fill:_balance]"
          data-testid="gallery-grid"
        >
          {shown.map((item, i) => (
            <button
              key={item.id}
              data-testid={`gallery-item-${i}`}
              onClick={() => setActive(i)}
              className="group relative mb-4 w-full break-inside-avoid overflow-hidden rounded-2xl border border-white/5 bg-ink text-left card-hover"
            >
              <img
                src={mediaSrc(item)}
                alt={item.title}
                loading="lazy"
                className="w-full h-auto object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              {item.type === "video" && (
                <span className="absolute inset-0 flex items-center justify-center">
                  <span className="w-14 h-14 rounded-full bg-mustard/90 text-ink flex items-center justify-center shadow-lg">
                    <Play className="w-6 h-6 ml-0.5" />
                  </span>
                </span>
              )}
              <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all">
                <span className="tag-emerald rounded-md px-2 py-0.5 text-[10px] font-mono2 uppercase tracking-wider">
                  {item.category}
                </span>
                <p className="mt-2 font-sub font-semibold text-sm text-cream">
                  {item.title}
                </p>
              </div>
            </button>
          ))}
        </div>
        {shown.length === 0 && (
          <p
            className="mt-8 text-cream/40 font-mono2 text-sm"
            data-testid="gallery-empty"
          >
            Aucun média pour le moment.
          </p>
        )}
      </div>

      {active !== null && shown[active] && (
        <Lightbox
          item={shown[active]}
          onClose={() => setActive(null)}
          onPrev={() => setActive((active - 1 + shown.length) % shown.length)}
          onNext={() => setActive((active + 1) % shown.length)}
        />
      )}
    </section>
  );
}

function Lightbox({ item, onClose, onPrev, onNext }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose, onPrev, onNext]);

  return (
    <div
      data-testid="gallery-lightbox"
      className="fixed inset-0 z-[100] bg-ink/95 backdrop-blur-md flex items-center justify-center p-4"
      onClick={onClose}
    >
      <button
        data-testid="lightbox-close"
        onClick={onClose}
        className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white/10 hover:bg-mustard hover:text-ink flex items-center justify-center transition"
      >
        <X className="w-5 h-5" />
      </button>
      <button
        data-testid="lightbox-prev"
        onClick={(e) => {
          e.stopPropagation();
          onPrev();
        }}
        className="absolute left-3 md:left-6 w-11 h-11 rounded-full bg-white/10 hover:bg-mustard hover:text-ink flex items-center justify-center transition"
      >
        <ChevronLeft className="w-5 h-5" />
      </button>
      <button
        data-testid="lightbox-next"
        onClick={(e) => {
          e.stopPropagation();
          onNext();
        }}
        className="absolute right-3 md:right-6 w-11 h-11 rounded-full bg-white/10 hover:bg-mustard hover:text-ink flex items-center justify-center transition"
      >
        <ChevronRight className="w-5 h-5" />
      </button>
      <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
        {item.type === "video" ? (
          <div className="aspect-video w-full rounded-2xl overflow-hidden border border-white/10">
            <iframe
              data-testid="lightbox-video"
              src={`${item.embed_url}?autoplay=1`}
              title={item.title}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        ) : (
          <img
            data-testid="lightbox-image"
            src={mediaSrc(item)}
            alt={item.title}
            className="w-full max-h-[80vh] object-contain rounded-2xl"
          />
        )}
        <div className="mt-4 flex items-center gap-3">
          <span className="tag-emerald rounded-md px-2 py-0.5 text-[10px] font-mono2 uppercase tracking-wider">
            {item.category}
          </span>
          <p
            className="font-display font-bold text-lg text-cream"
            data-testid="lightbox-title"
          >
            {item.title}
          </p>
        </div>
      </div>
    </div>
  );
}
